import {
  Button,
  Column,
  Copy,
  Flex,
  Link,
  Row,
  Section,
  Square,
  Squish,
  Stack,
  SvgCheck,
  SvgInfo,
  SvgUpload,
} from '@grubhubprod/gh-cookbook-react';
import { subscribe } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import classnames from 'classnames';
import React, { ReactNode, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { CSVUploadErrorBanner } from 'src/components/CSVUploadTracking/CSVUploadErrorBanner';
import {
  CSVUploadStatus,
  getErrorDetails,
  getUniqueAndSortedErrors,
  getUploadingProgressYValue,
  UploadStatus,
  useCSVUpload,
} from 'src/components/CSVUploadTracking/CSVUploadTracking.utils';
import { getConfig } from 'src/utilities/config';
import { getSingularOrPlural } from 'src/utilities/getSingularOrPlural';

export interface CSVUploadTrackingProps {
  corpId: string;
  updateCurrentUploadStatusCb?: (statusProps: UploadStatus) => void;
  uploadedRecordsTotalCb?: (dinersCreatedCount: number) => void;
  onSuccess?: () => void;
  onError?: (errors?: string[]) => void;
  showTryAgainBtn?: boolean;
  allowMultipleUploads?: boolean;
  acceptedCsvFileCb?: (acceptedCsvFile: File) => void;
  csvUploadFile?: string;
  header?: () => ReactNode;
  showUploadErrorBanner?: boolean;
  allowMultipleParralelFiles?: boolean;
}
export const RESET_UPLOAD_CSV_PROCESS = 'csvUploadSection:resetUploadProcess';

export function CSVUploadTracking({
  corpId,
  uploadedRecordsTotalCb,
  onError,
  onSuccess,
  showTryAgainBtn,
  updateCurrentUploadStatusCb,
  acceptedCsvFileCb,
  csvUploadFile = 'csv-upload-template.csv',
  header,
  allowMultipleUploads,
  showUploadErrorBanner = false,
  allowMultipleParralelFiles = true,
}: CSVUploadTrackingProps): JSX.Element {
  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
  } = useDropzone({
    accept: '.csv,text/csv',
    maxFiles: 1,
    multiple: allowMultipleParralelFiles,
    noClick: true,
    noKeyboard: true,
  });
  const { startCsvUpload, reset, currentUploadStatus } = useCSVUpload(corpId);
  const [acceptedCsvFile, setAcceptedCsvFile] = useState<File>(null);
  const configuration = getConfig();
  const showUploadAnotherCsvBtn =
    allowMultipleUploads && currentUploadStatus.isSuccessful;

  const areKnownErrorsExist =
    currentUploadStatus.lineErrors.length > 0 ||
    currentUploadStatus.fileErrors.length > 0;

  const errorMessage = getErrorDetails(
    areKnownErrorsExist,
    currentUploadStatus.exceedsMaxErrors
  );

  useEffect(() => {
    handleUploadStatus(currentUploadStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUploadStatus]);

  useEffect(() => {
    const subscription = subscribe(RESET_UPLOAD_CSV_PROCESS, () => {
      resetUploadProcess();
    });

    return () => {
      subscription();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (acceptedFiles.length === 1) {
      const file = acceptedFiles[0];
      setAcceptedCsvFile(file);
      if (acceptedCsvFileCb) {
        acceptedCsvFileCb(file);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFiles]);

  useEffect(() => {
    if (acceptedCsvFile) {
      startCsvUpload(acceptedCsvFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedCsvFile]);

  function handleUploadStatus(uploadStatus: UploadStatus) {
    if (updateCurrentUploadStatusCb) {
      updateCurrentUploadStatusCb(uploadStatus);
    }

    switch (uploadStatus.status) {
      case CSVUploadStatus.COMPLETE: {
        if (uploadedRecordsTotalCb) {
          uploadedRecordsTotalCb(uploadStatus.totalRecords);
        }

        if (onSuccess) {
          onSuccess();
        }
        break;
      }
      case CSVUploadStatus.ERROR: {
        if (onError) {
          onError(getUniqueAndSortedErrors(uploadStatus.lineErrors));
        }
        if (uploadedRecordsTotalCb) {
          uploadedRecordsTotalCb(uploadStatus.totalRecords);
        }
        break;
      }
      case CSVUploadStatus.FILE_ERROR: {
        if (onError) {
          onError(getUniqueAndSortedErrors(uploadStatus.fileErrors));
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  function resetUploadProcess() {
    setAcceptedCsvFile(null);
    if (acceptedCsvFileCb) {
      acceptedCsvFileCb(null);
    }
    reset();
  }

  return (
    <>
      {header ? (
        header()
      ) : (
        <Stack y="md">
          <Copy variant="body" textAlign="left">
            <div>
              Make sure your file fits{' '}
              <Link
                href={`${configuration.csvGuidelinesURL}`}
                rel="noreferrer noopener"
                target="_blank"
              >
                our guidelines
              </Link>{' '}
              or you can use{' '}
              <Link
                href={`${configuration.assetsURL}/${csvUploadFile}`}
                download={csvUploadFile}
              >
                our template
              </Link>{' '}
              to create one.
            </div>
          </Copy>
        </Stack>
      )}
      {!acceptedCsvFile ? (
        <Stack y="sm">
          <Flex
            alignItems="center"
            justifyContent="center"
            border="1px dashed"
            borderColor={isDragActive ? 'interactive' : 'secondaryText'}
            className={classnames('', {
              csvUploadActiveDrag: isDragActive,
            })}
            borderRadius="4px"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...getRootProps()}
          >
            <Section inset="md">
              <Copy variant="body" textAlign="center" color="secondaryText">
                <Stack y="sm">
                  <SvgUpload width="36px" height="36px" />
                </Stack>
                <div>
                  Drag and drop your CSV file here, or{' '}
                  <label htmlFor="csvFile">
                    <Button
                      variant="tertiary"
                      onClick={openFileDialog}
                      className="removeButtonPadding"
                    >
                      <Copy variant="body" color="interactive">
                        browse
                      </Copy>
                    </Button>
                    <input
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...getInputProps()}
                      data-testid="file-upload-input"
                    />
                  </label>
                </div>
              </Copy>
            </Section>
          </Flex>
        </Stack>
      ) : (
        <>
          {!currentUploadStatus.hasError && (
            <Squish inset="lg" dimension="1" borderRadius="4px">
              <Stack y="x-sm">
                <Flex justifyContent="space-between" alignItems="center">
                  <Copy variant="body" color="primaryText">
                    {acceptedCsvFile.name}
                  </Copy>
                  {currentUploadStatus.hasError ? (
                    <Copy variant="body" color="danger">
                      <SvgInfo />
                    </Copy>
                  ) : (
                    <Copy variant="body" color="primaryText">
                      {currentUploadStatus.isSuccessful && (
                        <Stack as="span" x="x-sm">
                          <Copy variant="body" color="success">
                            <SvgCheck />
                          </Copy>
                        </Stack>
                      )}
                      {currentUploadStatus.progress}%
                    </Copy>
                  )}
                </Flex>
              </Stack>
              <Stack
                y={getUploadingProgressYValue(
                  showUploadAnotherCsvBtn,
                  currentUploadStatus.totalRecords
                )}
              >
                <div className="csvUploadProgressBarBackground">
                  <div
                    role="progressbar"
                    aria-label="Upload progress"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={currentUploadStatus.progress}
                    className={classnames('csvUploadProgressBarValue', {
                      hasError: currentUploadStatus.hasError,
                    })}
                    style={{ width: `${currentUploadStatus.progress}%` }}
                  />
                </div>
              </Stack>
              {!!currentUploadStatus.totalRecords && (
                <Stack y={showUploadAnotherCsvBtn ? 'x-sm' : 'none'}>
                  <Flex alignItems="center" justifyContent="end">
                    <Copy variant="body" color="success">
                      {`${
                        currentUploadStatus.totalRecords
                      } ${getSingularOrPlural(
                        currentUploadStatus.totalRecords,
                        'employee'
                      )} added successfully`}
                    </Copy>
                  </Flex>
                </Stack>
              )}
              {showUploadAnotherCsvBtn && (
                <Flex alignItems="center" justifyContent="center">
                  <Button
                    variant="tertiary"
                    type="button"
                    buttonSize="medium"
                    className="removeButtonPadding"
                    onClick={resetUploadProcess}
                  >
                    Upload another CSV
                  </Button>
                </Flex>
              )}
            </Squish>
          )}
          {currentUploadStatus.hasError && (
            <>
              {showUploadErrorBanner && acceptedCsvFile && (
                <CSVUploadErrorBanner
                  fileName={acceptedCsvFile.name}
                  areKnownErrorsExist={areKnownErrorsExist}
                  exceedsMaxErrors={currentUploadStatus.exceedsMaxErrors}
                />
              )}
              <Squish
                inset="lg"
                borderRadius="4px"
                border="1px solid"
                borderColor="lineMedium"
              >
                <Stack y="x-sm">
                  <Copy variant="body" color="danger">
                    <Flex alignItems="center">
                      <Stack x="x-sm">
                        <SvgInfo size="24px" />
                      </Stack>
                      <Copy
                        variant="lead"
                        fontFamily="secondary"
                        color="danger"
                      >
                        Something went wrong
                      </Copy>
                    </Flex>
                  </Copy>
                </Stack>
                <Stack y="sm">
                  <Copy variant="body">{errorMessage}</Copy>
                </Stack>
                {currentUploadStatus.fileErrors.length > 0 && (
                  <Stack y="sm">
                    {currentUploadStatus.fileErrors.map(({ id, message }) => (
                      <Copy key={id} variant="body">
                        {message}
                      </Copy>
                    ))}
                  </Stack>
                )}
                {currentUploadStatus.lineErrors.length > 0 && (
                  <Stack as="ul" y="sm" className="csvUploadErrorsList">
                    {currentUploadStatus.lineErrors.map(
                      ({ id, line, message }) => (
                        <Square
                          key={id}
                          as="li"
                          inset="x-sm"
                          borderBottom="1px solid"
                          borderColor="lightLine"
                        >
                          <Row gutter={4}>
                            <Column gutter={4} phone={4}>
                              <Copy variant="body">Row {line}</Copy>
                            </Column>
                            <Column gutter={4} phone={8}>
                              <Copy variant="body">{message}</Copy>
                            </Column>
                          </Row>
                        </Square>
                      )
                    )}
                  </Stack>
                )}
                {showTryAgainBtn && (
                  <Button variant="primary" onClick={resetUploadProcess}>
                    Try again
                  </Button>
                )}
              </Squish>
            </>
          )}
        </>
      )}
    </>
  );
}
